/* Scroll video split — "text sweep" entrance animation for the text caption
 *
 * When the right column's "text caption" block (<svs-text-reveal>) scrolls into the viewport, the title is swept character by character
 * from the base color (gray) to the target color, and the description then fades into place as a whole. Based on the SOULOG-Hero text-sweep
 * prototype: each character has its own progress window, adjacent characters overlap by spread, with smoothstep easing.
 *
 * Design trade-offs:
 * - No dependency on GSAP / ScrollTrigger (this section's per-frame engine is already heavy); a single shared
 *   rAF-throttled scroll listener drives all instances.
 * - The target color is read with getComputedStyle before splitting, so the sweep still lands correctly when the admin "text caption color" is left blank
 *   and falls back to the theme default; no need to duplicate the theme's color fallback chain in JS.
 * - Splitting groups characters by "word" (white-space:nowrap inside .svs-rw), so per-character inline-block
 *   never breaks a word in the middle.
 * - The description uses a whole-block color transition rather than per character: the body is rich text and may contain <strong>/<a>,
 *   so recursively splitting nodes isn't worth it.
 * - With prefers-reduced-motion it doesn't start at all; the text keeps the author's static styling.
 */
(function () {
	"use strict";

	if (!("customElements" in window)) return;

	var REDUCED = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

	/* Progress window: the scroll during which the title's top edge moves from 82% to 34% of the viewport = progress 0 → 1. */
	var START_VH = 0.82;
	var END_VH = 0.34;
	/* The description only starts fading after the title sweep reaches 60%, finishing at 100%. */
	var BODY_FROM = 0.6;

	var STYLES = { fade: 1, rise: 1, drop: 1, scale: 1, blur: 1 };

	/* '#abc' / '#aabbcc' / 'rgb(a, b, c)' / 'rgba(...)' → [r,g,b,a]; returns null on failure.
	   Alpha must be kept: the unified section description color is rgba(var(--color-body-rgb), .7);
	   if alpha were dropped here, the sweep would end by jumping to an opaque solid color, noticeably darker than the static description. */
	function toRgb(str) {
		if (!str) return null;
		str = String(str).trim();
		if (str.charAt(0) === "#") {
			var h = str.slice(1);
			if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
			if (h.length !== 6) return null;
			var n = parseInt(h, 16);
			return isNaN(n) ? null : [(n >> 16) & 255, (n >> 8) & 255, n & 255, 1];
		}
		var m = str.match(/-?\d+(\.\d+)?/g);
		if (!m || m.length < 3) return null;
		return [+m[0], +m[1], +m[2], m.length > 3 ? +m[3] : 1];
	}

	function mix(from, to, e) {
		var fa = from.length > 3 ? from[3] : 1;
		var ta = to.length > 3 ? to[3] : 1;
		var a = fa + (ta - fa) * e;
		return (
			"rgba(" +
			Math.round(from[0] + (to[0] - from[0]) * e) +
			"," +
			Math.round(from[1] + (to[1] - from[1]) * e) +
			"," +
			Math.round(from[2] + (to[2] - from[2]) * e) +
			"," +
			Math.round(a * 1000) / 1000 +
			")"
		);
	}

	function clamp01(v) {
		return v < 0 ? 0 : v > 1 ? 1 : v;
	}

	/* --- Shared scroll driver --- */
	var instances = [];
	var ticking = false;

	function tick() {
		ticking = false;
		for (var i = 0; i < instances.length; i++) instances[i].update();
	}

	function onScroll() {
		if (ticking) return;
		ticking = true;
		requestAnimationFrame(tick);
	}

	function attach(inst) {
		instances.push(inst);
		if (instances.length === 1) {
			window.addEventListener("scroll", onScroll, { passive: true });
			window.addEventListener("resize", onScroll);
		}
	}

	function detach(inst) {
		var i = instances.indexOf(inst);
		if (i >= 0) instances.splice(i, 1);
		if (!instances.length) {
			window.removeEventListener("scroll", onScroll);
			window.removeEventListener("resize", onScroll);
		}
	}

	class SvsTextRevealElement extends HTMLElement {
		connectedCallback() {
			if (this.__svsReady) {
				if (this.__svsActive) attach(this);
				return;
			}
			this.__svsReady = true;
			if (REDUCED || this.dataset.reveal === "false") return;

			this.headingEl = this.querySelector("h3, .h2");
			this.bodyEl = this.querySelector(".rte");
			if (!this.headingEl && !this.bodyEl) return;

			this.base = toRgb(this.dataset.baseColor) || [214, 214, 219];
			this.spread = parseFloat(this.dataset.spread);
			if (!(this.spread > 0)) this.spread = 0.55;
			this.style_ = STYLES[this.dataset.style] ? this.dataset.style : "rise";

			/* The target color must be read before writing any inline color, otherwise what's read back is the base color. */
			if (this.headingEl) this.headingTarget = toRgb(getComputedStyle(this.headingEl).color) || [29, 29, 31];
			if (this.bodyEl) this.bodyTarget = toRgb(getComputedStyle(this.bodyEl).color) || [29, 29, 31];

			this.chars = this.headingEl ? this.splitChars(this.headingEl) : [];
			if (!this.chars.length && !this.bodyEl) return;

			this.__svsActive = true;
			attach(this);
			this.update();
		}

		disconnectedCallback() {
			if (this.__svsActive) detach(this);
		}

		/* Split the title into two levels, word → character: the outer .svs-rw ensures line breaks only happen between words. */
		splitChars(el) {
			var text = el.textContent;
			if (!text || !text.trim()) return [];
			/* After splitting, every character is its own node and screen readers may read them one by one -- give the container a complete
			   aria-label and mark the inner content as decorative. */
			el.setAttribute("aria-label", text.trim());
			var frag = document.createDocumentFragment();
			var chars = [];
			var words = text.split(/(\s+)/);
			for (var w = 0; w < words.length; w++) {
				var word = words[w];
				if (!word) continue;
				var wrap = document.createElement("span");
				wrap.className = "svs-rw";
				wrap.setAttribute("aria-hidden", "true");
				var glyphs = Array.from(word);
				for (var g = 0; g < glyphs.length; g++) {
					var span = document.createElement("span");
					span.className = "svs-rc";
					/* The space must be a plain U+0020: an en space (U+2002, fixed 0.5em) was once used to keep
					   whitespace from collapsing, but .svs-rc already has white-space: pre, so it can't collapse anyway;
					   an en space is nearly twice as wide as Ubuntu's normal space (≈0.26em), which spread the title's word spacing
					   apart, so next to the product-showcase-capture title (which has no reveal) it was obviously not the same
					   typography (fixed 2026-07-22). */
					span.textContent = /\s/.test(glyphs[g]) ? " " : glyphs[g];
					wrap.appendChild(span);
					chars.push(span);
				}
				frag.appendChild(wrap);
			}
			el.textContent = "";
			el.appendChild(frag);
			return chars;
		}

		update() {
			var anchor = this.headingEl || this.bodyEl;
			if (!anchor) return;
			var vh = window.innerHeight || document.documentElement.clientHeight;
			var rect = anchor.getBoundingClientRect();
			/* When the element is display:none (e.g. hidden on mobile) the rect is all 0; skip it. */
			if (!rect.height && !rect.width) return;

			var start = vh * START_VH;
			var end = vh * END_VH;
			var raw = clamp01((start - rect.top) / (start - end));
			var spread = this.spread;
			var n = this.chars.length || 1;
			var half = spread / 2 + 0.5 / n;

			/* Remap the 0→1 scroll progress to -spread/2 → 1+spread/2.
			   The prototype used 0→1+spread directly, so the first character's window started at -spread/2,
			   meaning at progress 0 the first character was already ~40% swept (the pure base color was never visible), and the last character
			   only finished after scrolling past the end. Aligned to the true boundaries of the character windows, the two ends of the scroll window are exactly
			   "no character swept yet" and "the last character just fully swept". */
			var p = raw * (1 + spread) - spread / 2;
			var style = this.style_;
			var base = this.base;
			var target = this.headingTarget;

			for (var i = 0; i < this.chars.length; i++) {
				var el = this.chars[i];
				var center = (i + 0.5) / n;
				var e = smooth(clamp01((p - (center - half)) / (2 * half)));
				var inv = 1 - e;
				el.style.color = mix(base, target, e);
				if (style === "rise") {
					el.style.transform = "translateY(" + (0.22 * inv).toFixed(3) + "em)";
				} else if (style === "drop") {
					el.style.transform = "translateY(" + (-0.3 * inv).toFixed(3) + "em)";
				} else if (style === "scale") {
					el.style.transform = "scale(" + (0.7 + 0.3 * e).toFixed(3) + ")";
				} else if (style === "blur") {
					el.style.filter = "blur(" + (5 * inv).toFixed(2) + "px)";
					el.style.opacity = (0.2 + 0.8 * e).toFixed(3);
				}
			}

			if (this.bodyEl) {
				/* Use raw rather than the remapped p: the description must finish at the same moment as the title. */
				var bp = clamp01((raw - BODY_FROM) / (1 - BODY_FROM));
				this.bodyEl.style.color = mix(base, this.bodyTarget, smooth(bp));
			}
		}
	}

	function smooth(t) {
		return t * t * (3 - 2 * t);
	}

	if (!customElements.get("svs-text-reveal")) {
		customElements.define("svs-text-reveal", SvsTextRevealElement);
	}
})();
